import React, { useEffect, useState } from "react";
import axios from "axios";
import webservice from "../../../services/webservice";
import { AesEncrypt, AesDecrypt } from "../../../services/crypto";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { fetchScreenInsights } from "../Configure/store";
import { ComponentLoader } from '../common/Loader';
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Legend
} from "recharts";
import { FaArrowLeft, FaChartPie, FaChartBar, FaUserTie } from "react-icons/fa";
import Swal from "sweetalert2";
import "./analytics-master.css";

const COLORS = ["#6366f1", "#ec4899", "#10b981", "#f59e0b", "#3b82f6", "#8b5cf6", "#f43f5e"];

const ScreenInsights = () => {
    const { screenName: urlScreenName } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [loading, setLoading] = useState(!!urlScreenName);
    const [data, setData] = useState(null);
    const [screenList, setScreenList] = useState([]);
    const [fetchingScreens, setFetchingScreens] = useState(false);

    useEffect(() => {
        fetchScreens(); // Always fetch screens to populate dropdowns
        if (urlScreenName) {
            loadInsights(urlScreenName);
        } else {
            setLoading(false);
            setData(null);
        }
    }, [urlScreenName]);

    const fetchScreens = async () => {
        try {
            const cachedData = sessionStorage.getItem('SCREEN_LIST_CACHE');
            let cacheExists = false;

            if (cachedData) {
                const parsed = JSON.parse(cachedData);
                if (parsed === 'PENDING') return;

                if (parsed && typeof parsed === 'object') {
                    const cacheTimestamp = parsed.cacheTimestamp || 0;
                    const threeMinutes = 3 * 60 * 1000;
                    const isExpired = Date.now() - cacheTimestamp > threeMinutes;

                    if (!isExpired) {
                        setScreenList(parsed.screens || []);
                        setFetchingScreens(false);
                        return;
                    } else {
                        setScreenList(parsed.screens || []);
                        cacheExists = true;
                    }
                }
            }

            if (!cacheExists) {
                setFetchingScreens(true);
            }

            sessionStorage.setItem('SCREEN_LIST_CACHE', JSON.stringify('PENDING'));
            const encryptedPayload = AesEncrypt({});
            const response = await axios.post(`${webservice}api/screen-auditlog`, {
                EncData: encryptedPayload,
            });

            if (response.data.response === 'S') {
                const decrypted = AesDecrypt(response.data.data);
                const screens = decrypted.topScreens || [];
                setScreenList(screens);
                sessionStorage.setItem('SCREEN_LIST_CACHE', JSON.stringify({
                    screens: screens,
                    cacheTimestamp: Date.now()
                }));
            } else {
                sessionStorage.removeItem('SCREEN_LIST_CACHE');
            }
        } catch (err) {
            console.error("Failed to fetch screens:", err);
            sessionStorage.removeItem('SCREEN_LIST_CACHE');
        } finally {
            setFetchingScreens(false);
        }
    };

    const loadInsights = (name) => {
        const cacheKey = `SCREEN_INSIGHTS_CACHE_${name}`;
        const cachedData = sessionStorage.getItem(cacheKey);
        let cacheExists = false;

        if (cachedData) {
            const parsed = JSON.parse(cachedData);
            if (parsed === 'PENDING') {
                return;
            }

            if (parsed && typeof parsed === 'object') {
                const cacheTimestamp = parsed.cacheTimestamp || 0;
                const threeMinutes = 3 * 60 * 1000;
                const isExpired = Date.now() - cacheTimestamp > threeMinutes;

                if (!isExpired) {
                    setData(parsed.insights);
                    setLoading(false);
                    return;
                } else {
                    setData(parsed.insights);
                    setLoading(false);
                    cacheExists = true;
                }
            }
        }

        if (!cacheExists) {
            setLoading(true);
        }

        sessionStorage.setItem(cacheKey, JSON.stringify('PENDING'));

        dispatch(fetchScreenInsights({ screenName: name }))
            .then((response) => {
                if (response.payload && response.payload.success) {
                    setData(response.payload.insights);
                    sessionStorage.setItem(cacheKey, JSON.stringify({
                        insights: response.payload.insights,
                        cacheTimestamp: Date.now()
                    }));
                } else {
                    Swal.fire("Error", "Failed to load screen insights", "error");
                    navigate("/screen-auditlog");
                    sessionStorage.removeItem(cacheKey);
                }
                setLoading(false);
            })
            .catch((error) => {
                console.error("Error fetching insights:", error);
                Swal.fire("Error", "Something went wrong", "error");
                sessionStorage.removeItem(cacheKey);
                setLoading(false);
            });
    };

    const handleScreenChange = (e) => {
        const selected = e.target.value;
        if (selected) {
            navigate(`/screen-insights/${encodeURIComponent(selected)}`);
        }
    };

    if (loading) {
        return (
            <div className="analytics-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <ComponentLoader color="#4f46e5" size="40px" />
            </div>
        );
    }

    if (!data) {
        return (
            <div className="analytics-wrapper" style={{ flexDirection: 'column', padding: '2rem' }}>
                <div className="insight-header" style={{ display: 'flex', alignItems: 'center', marginBottom: '2rem', gap: '1.5rem', justifyContent: 'space-between' }}>
                    <div>
                        <h2 style={{ margin: 0, fontWeight: 'bold' }}>Screen Wise Filter</h2>
                        <p style={{ margin: 0, color: 'var(--text-muted)' }}>Select a screen to view detailed demographic insights</p>
                    </div>
                    <button className="analytics-btn analytics-btn--primary" onClick={() => navigate("/screen-auditlog")} style={{ padding: '0.5rem 1rem' }}>
                        <FaArrowLeft /> Back
                    </button>
                </div>

                <div className="stat-card" style={{ maxWidth: '600px', margin: '0 auto', padding: '3rem' }}>
                    <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--text-secondary)' }}>
                            Choose Screen
                        </label>
                        <select
                            className="demo-modern-filter-select"
                            style={{ width: '100%', padding: '1rem', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: '1rem' }}
                            onChange={handleScreenChange}
                            value=""
                            disabled={fetchingScreens}
                        >
                            <option value="">{fetchingScreens ? "Fetching screens..." : "Select a screen..."}</option>
                            {screenList.map((screen, idx) => (
                                <option key={idx} value={screen.name}>{screen.name}</option>
                            ))}
                        </select>
                    </div>
                    <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', textAlign: 'center' }}>
                        Insights include gender, age, marital status, and profession distribution of users.
                    </p>
                </div>
            </div>
        );
    }

    const { genderDistribution, maritalStatusDistribution, professionDistribution, ageDistribution } = data;

    const formatAgeLabel = (id) => {
        if (id === "Other") return "Other";
        const boundaries = [0, 18, 25, 35, 45, 60, 100];
        const idx = boundaries.indexOf(id);
        if (idx !== -1 && idx < boundaries.length - 1) {
            return `${boundaries[idx]}-${boundaries[idx + 1]}`;
        }
        return id.toString();
    };

    const formattedAgeData = ageDistribution.map(item => ({
        name: formatAgeLabel(item._id),
        count: item.count
    }));

    const hasData = (arr) => arr && arr.length > 0 && arr.some(item => item.count > 0);

    const totalInteractions = genderDistribution.reduce((acc, curr) => acc + curr.count, 0);

    const EmptyState = ({ title }) => (
        <div style={{ height: '300px', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', position: 'relative' }}>
            <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                    <Pie
                        data={[{ value: 100 }]}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={0}
                        dataKey="value"
                        isAnimationActive={false}
                    >
                        <Cell fill="#e2e8f0" />
                    </Pie>
                </PieChart>
            </ResponsiveContainer>
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                <p style={{ margin: 0, fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', fontSize: '0.8rem' }}>No Data</p>
                <p style={{ margin: 0, fontSize: '0.7rem', color: '#cbd5e1' }}>Available</p>
            </div>
        </div>
    );

    return (
        <div className="analytics-wrapper" style={{ flexDirection: 'column', padding: '2rem' }}>
            <button
                className="analytics-btn analytics-btn--primary"
                onClick={() => navigate("/screen-auditlog")}
                style={{
                    padding: "0.5rem 1rem",
                    display: "block",
                    marginRight: "auto",
                    marginBottom: "2rem"
                }}
            >
                <FaArrowLeft /> Back
            </button>

            <div className="insight-header" style={{ display: 'flex', alignItems: 'center', marginBottom: '2rem', gap: '1.5rem' }}>

                <div>
                    <h2 style={{ margin: 0, fontWeight: 'bold' }}>{urlScreenName} Insights</h2>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginTop: '1.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'var(--bg-card)', padding: '0.2rem 0.6rem', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
                            <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Switch Screen:</span>
                            <select
                                style={{
                                    padding: '0.3rem 0.5rem',
                                    borderRadius: '4px',
                                    border: 'none',
                                    background: 'transparent',
                                    fontSize: '0.85rem',
                                    fontWeight: '600',
                                    color: 'var(--accent)',
                                    cursor: 'pointer',
                                    outline: 'none'
                                }}
                                onChange={handleScreenChange}
                                value={urlScreenName}
                            >
                                <option value="">Select...</option>
                                {screenList.map((screen, idx) => (
                                    <option key={idx} value={screen.name}>{screen.name}</option>
                                ))}
                            </select>
                        </div>
                        <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.9rem' }}>Demographic breakdown of users</p>
                    </div>
                </div>
                <div style={{ marginLeft: 'auto', background: 'var(--bg-card)', padding: '0.75rem 1.5rem', borderRadius: 'var(--radius)', border: '1px solid var(--border)', textAlign: 'center' }}>
                    <span style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Total Users Interactions</span>
                    <span style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{totalInteractions.toLocaleString()}</span>
                </div>
            </div>

            <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', marginTop: '0.5rem' }}>
                {/* Gender Distribution */}
                <div className="stat-card" style={{ display: 'flex', flexDirection: 'column' }}>
                    <div className="stat-text" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <FaChartPie style={{ color: 'var(--accent)' }} />
                        <h3 style={{ margin: 0 }}>Gender Distribution</h3>
                    </div>
                    {hasData(genderDistribution) ? (
                        <div style={{ height: '300px', width: '100%' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={genderDistribution}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={100}
                                        paddingAngle={5}
                                        dataKey="count"
                                        label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                                    >
                                        {genderDistribution.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <EmptyState title="Gender" />
                    )}
                </div>

                {/* Marital Status Distribution */}
                <div className="stat-card" style={{ display: 'flex', flexDirection: 'column' }}>
                    <div className="stat-text" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <FaChartPie style={{ color: '#ec4899' }} />
                        <h3 style={{ margin: 0 }}>Marital Status</h3>
                    </div>
                    {hasData(maritalStatusDistribution) ? (
                        <div style={{ height: '300px', width: '100%' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={maritalStatusDistribution}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={100}
                                        paddingAngle={5}
                                        dataKey="count"
                                        label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                                    >
                                        {maritalStatusDistribution.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[(index + 2) % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <EmptyState title="Marital Status" />
                    )}
                </div>

                {/* Age Distribution */}
                <div className="stat-card" style={{ display: 'flex', flexDirection: 'column' }}>
                    <div className="stat-text" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <FaChartBar style={{ color: '#10b981' }} />
                        <h3 style={{ margin: 0 }}>Age Distribution</h3>
                    </div>
                    {hasData(ageDistribution) ? (
                        <div style={{ height: '300px', width: '100%' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={formattedAgeData}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="name" />
                                    <YAxis />
                                    <Tooltip />
                                    <Bar dataKey="count" fill="#10b981" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <EmptyState title="Age" />
                    )}
                </div>

                {/* Profession Distribution */}
                <div className="stat-card" style={{ display: 'flex', flexDirection: 'column' }}>
                    <div className="stat-text" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <FaUserTie style={{ color: '#f59e0b' }} />
                        <h3 style={{ margin: 0 }}>Top Professions</h3>
                    </div>
                    <div className="profession-list" style={{ flex: 1, overflowY: 'auto' }}>
                        {hasData(professionDistribution) ? (
                            <table style={{ width: '100%' }}>
                                <thead>
                                    <tr style={{ background: 'transparent' }}>
                                        <th style={{ padding: '0.5rem 0', color: 'var(--text-muted)', fontSize: '0.75rem' }}>Profession</th>
                                        <th style={{ padding: '0.5rem 0', textAlign: 'right', color: 'var(--text-muted)', fontSize: '0.75rem' }}>Interactions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {professionDistribution.map((item, idx) => (
                                        <tr key={idx} style={{ borderBottom: '1px solid var(--border)' }}>
                                            <td style={{ padding: '0.75rem 0', textTransform: 'capitalize' }}>{item.name || "Unknown"}</td>
                                            <td style={{ padding: '0.75rem 0', textAlign: 'right', fontWeight: 'bold' }}>{item.count.toLocaleString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <EmptyState title="Professions" />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ScreenInsights;

